import {
  AppUiRecordDataService,
  hasUnsavedChanges,
} from '@omni/data-services/app-ui/app-ui-record.data.service';
import {
  ORDER_SCREEN_ENTITY,
  ORDER_SCREEN_KEY,
  OrderCaptureDataService,
  PULL_LIMIT,
} from '@omni/data-services/order-capture/order-capture.data.service';
import {
  PulledOrder,
  ViewedOrder,
  toPulledOrder,
  toViewedOrder,
} from './server-order';
import { AppUiMoneyFormatter } from '@omni/services/app-ui/app-ui-money-formatter';
import { AppUiRecordDoc } from '@omni/interfaces/app-ui/record.interface';
import { AppUiScreenDataService } from '@omni/data-services/app-ui/app-ui-screen.data.service';
import { DeviceService } from '@omni/services/device/device.service';
import { Injectable } from '@angular/core';
import { MyOrdersService } from './order-list/my-orders.service';
import { filledSequences } from './order-draft';
import { TranslateService } from '@ngx-translate/core';
import { idOf, isGuid } from '@omni/utility/dynamics-row.util';
import { indexControls } from '@omni/services/app-ui/app-ui-merge';
import { resolveOverlayScreen } from '@omni/interfaces/app-ui/overlay.interface';
import { draftsFetchXml } from './order-lists';

/**
 * A server change this close after the device last synced is taken to be the device's own Save, not
 * another device's edit: the SaveOrder reply carries no `modifiedon` to stamp.
 */
const OWN_SEND_SLACK_MS = 2 * 60 * 1000;

/**
 * The order's server copies on the device: the rep's open orders pulled into device drafts, so one
 * started on another device carries on here; the server's version of a draft when the rep picks
 * it; and any order read whole for a read-only view.
 */
@Injectable({ providedIn: 'root' })
export class ServerOrdersService {
  private pulling: Promise<unknown> = Promise.resolve();

  constructor(
    private readonly orders: OrderCaptureDataService,
    private readonly records: AppUiRecordDataService,
    private readonly screens: AppUiScreenDataService,
    private readonly deviceService: DeviceService,
    private readonly translate: TranslateService,
    private readonly myOrders: MyOrdersService,
    private readonly money: AppUiMoneyFormatter,
  ) {}

  /** The master sync's order step: the bundles, the rep's open orders, then all their orders for the tool. */
  public async syncInitialData(
    loadFromDbOnly = false,
    forceFullSync = false,
  ): Promise<void> {
    await this.orders.syncInitialData(loadFromDbOnly);
    // After the screen bundle, whose form decides which header fields a pulled draft keeps.
    await Promise.all([
      loadFromDbOnly
        ? null
        : this.pullMyOpenOrders().catch((error) =>
            console.warn(
              'ServerOrdersService: pulling open orders failed',
              error,
            ),
          ),
      this.myOrders.sync(forceFullSync, loadFromDbOnly),
    ]);
  }

  /**
   * The orders this rep created that are still open in Dynamics, merged into the device's drafts so
   * one started on another device carries on here. A device copy with anything unsent wins; a landed
   * one is replaced when the server's changed since. Online only; returns how many were taken.
   */
  public pullMyOpenOrders(accountId?: string | null): Promise<number> {
    // One at a time: overlapping pulls (sync and the drafts list) would each adopt the same order.
    const run = this.pulling.then(() => this.pullNow(accountId));
    this.pulling = run.catch(() => 0);
    return run;
  }

  /** The rep chose the server's version of a draft: it replaces the device copy. Online only. */
  public async takeServerCopy(record: AppUiRecordDoc): Promise<boolean> {
    if (this.deviceService.isOffline || !record.serverId) return false;
    const { orders, lines, deliveries } = await this.orders.fetchOpenOrders({
      orderId: record.serverId,
    });
    const pulled = orders[0]
      ? toPulledOrder(
          { order: orders[0], lines, deliveries },
          await this.headerAttributes(),
        )
      : null;
    // As the pull skips it: its lines would move onto a delivery the server never had.
    if (!pulled || pulled.withoutDeliveryRows) return false;
    return this.adopt(pulled, record.localId, true);
  }

  /**
   * Any order of this account, whatever its status or rep, read whole to be viewed read-only, with
   * the figures Dynamics holds. Online only; null when it cannot be read.
   */
  public async getOrderForView(orderId: string): Promise<ViewedOrder | null> {
    const rows = await this.orders.fetchOrderForView(orderId);
    return rows ? toViewedOrder(rows, await this.headerAttributes()) : null;
  }

  private async pullNow(accountId?: string | null): Promise<number> {
    if (!this.orders.isEnabled || this.deviceService.isOffline) return 0;
    if (accountId && !isGuid(accountId)) return 0;
    // Read before the fetch: a record landing meanwhile is still pending here, so not closed as gone.
    const local = (await this.records.list()).filter(
      (doc) => doc.entity === ORDER_SCREEN_ENTITY && !doc.isDeleted,
    );
    const { drafts, headerAttributes } = await this.pullFacts();
    const { orders, lines, deliveries } = await this.orders.fetchOpenOrders({
      accountId,
      drafts,
    });
    const byServerId = new Map(
      local
        .filter((doc) => doc.serverId)
        .map((doc) => [String(doc.serverId).toLowerCase(), doc]),
    );
    // The device mints the order's id, so a send whose answer was lost is still recognised.
    const byOrderId = new Map(
      local.map((doc) => [orderIdOf(doc), doc] as const).filter(([id]) => !!id),
    );

    let taken = 0;
    for (const order of orders) {
      const id = idOf(order.salesorderid);
      if (!byServerId.has(id) && byOrderId.has(id)) continue;
      const pulled = toPulledOrder(
        {
          order,
          lines: lines.filter((line) => idOf(line._salesorderid_value) === id),
          deliveries: deliveries.filter(
            (row) => idOf(row._indskr_salesorder_value) === id,
          ),
        },
        headerAttributes,
      );
      // Not one to edit here: its lines would have to move onto a delivery it never had.
      if (pulled?.withoutDeliveryRows) continue;
      if (pulled && (await this.take(pulled, byServerId.get(id)))) taken++;
    }
    // At the limit, an order missing from the answer may only be past it.
    const closed =
      orders.length < PULL_LIMIT
        ? await this.closeGone(
            local,
            new Set(orders.map((order) => idOf(order.salesorderid))),
            accountId,
            !!drafts,
          )
        : 0;
    // Orders moved on elsewhere: what an account ordered last may have too.
    if (taken || closed) this.orders.forgetLastOrders(accountId);
    return taken;
  }

  /**
   * Takes the server's copy unless the device's is current. A device copy with unsaved changes, or
   * open on screen (whose next change would write its own state back), is kept; if another device
   * saved the order meanwhile, it is flagged so opening it asks the rep.
   */
  private async take(
    pulled: PulledOrder,
    existing: AppUiRecordDoc | undefined,
  ): Promise<boolean> {
    const elsewhere =
      !!existing && changedElsewhere(existing, pulled.modifiedOn);
    if (
      existing &&
      (hasUnsavedChanges(existing) || this.records.isOnScreen(existing.localId))
    ) {
      if (elsewhere && existing.serverChangedOn !== pulled.modifiedOn)
        await this.records.flagServerChange(existing.localId, {
          modifiedOn: pulled.modifiedOn,
          total: this.totalOf(pulled),
          serverName: pulled.name,
        });
      return false;
    }
    if (existing && !elsewhere && !pulledBefore(existing, pulled)) {
      if (
        existing.serverModifiedOn !== pulled.modifiedOn ||
        !existing.serverName
      )
        await this.records.markServerVersion(
          existing.localId,
          pulled.modifiedOn,
          pulled.name,
        );
      return false;
    }
    return this.adopt(pulled, existing?.localId);
  }

  private async adopt(
    pulled: PulledOrder,
    localId?: string,
    force = false,
  ): Promise<boolean> {
    return this.records.adoptServerCopy(
      {
        localId: localId ?? this.records.newLocalId(),
        entity: ORDER_SCREEN_ENTITY,
        screenKey: ORDER_SCREEN_KEY,
        parentId: pulled.accountId,
        bundleVersion: null,
        asOf: pulled.snapshot.asOf,
        state: pulled.snapshot,
        values: pulled.values,
        summary: this.summaryOf(pulled),
        serverId: pulled.serverId,
        serverModifiedOn: pulled.modifiedOn,
        serverName: pulled.name,
        acknowledged: pulled.acknowledged,
      },
      force,
    );
  }

  private async headerAttributes(): Promise<string[]> {
    return (await this.pullFacts()).headerAttributes;
  }

  /**
   * From the screen's bundle: its drafts dataset (Pending Orders), so New Order and Pending list one
   * query's orders, and the header fields an order carries.
   */
  private async pullFacts(): Promise<{
    drafts: string | null;
    headerAttributes: string[];
  }> {
    const bundle = (await this.screens.getCachedBundle(ORDER_SCREEN_KEY))
      ?.bundle;
    return {
      drafts: draftsFetchXml(resolveOverlayScreen(bundle?.layout)?.dataSources),
      headerAttributes: [...indexControls(bundle?.formMetadata).keys()],
    };
  }

  /**
   * Submitted, cancelled or deleted elsewhere: no longer a draft here either. Returns how many. A
   * dataset may leave out orders still open, so after one (`confirm`) only those Dynamics no longer
   * holds open are closed: closing marks a draft submitted.
   */
  private async closeGone(
    local: readonly AppUiRecordDoc[],
    open: ReadonlySet<string>,
    accountId: string | null | undefined,
    confirm: boolean,
  ): Promise<number> {
    let gone = local.filter(
      (doc) =>
        doc.serverId &&
        doc.syncStatus === 'landed' &&
        !doc.submitted &&
        !open.has(idOf(doc.serverId)) &&
        (!accountId || doc.parentId === accountId),
    );
    if (confirm && gone.length) {
      const stillOpen = await this.orders
        .stillOpen(gone.map((doc) => idOf(doc.serverId)))
        .catch(() => null);
      gone = stillOpen
        ? gone.filter((doc) => !stillOpen.has(idOf(doc.serverId)))
        : [];
    }
    await this.records.closeLanded(gone.map((doc) => doc.localId));
    return gone.length;
  }

  private summaryOf(pulled: PulledOrder): string {
    const lineCount = Object.values(pulled.snapshot.lines).reduce(
      (sum, line) => sum + Object.keys(line.quantities).length,
      0,
    );
    const filled = filledSequences(pulled.snapshot.lines);
    return [
      pulled.snapshot.schedule?.name,
      filled.size
        ? this.translate.instant('OC_DELIVERY_COUNT', { count: filled.size })
        : null,
      this.translate.instant('OC_LINE_COUNT', { count: lineCount }),
      this.totalOf(pulled),
    ]
      .filter(Boolean)
      .join(' · ');
  }

  /** As the order screen shows money: Dynamics' own text can carry another precision. */
  private totalOf(pulled: PulledOrder): string {
    return this.money.format(pulled.snapshot.net ?? 0, pulled.currencyCode);
  }
}

/** The server's copy moved since the device last agreed with it, by more than the device's own Save. */
function changedElsewhere(
  existing: AppUiRecordDoc,
  serverModifiedOn: string,
): boolean {
  if (existing.serverModifiedOn === serverModifiedOn) return false;
  // The device's own Save moves the server's `modifiedon` too, a moment before it hears back.
  const lastSync = existing.syncedOn ?? existing.modifiedOn;
  return (
    Date.parse(serverModifiedOn) - Date.parse(lastSync) > OWN_SEND_SLACK_MS
  );
}

/** A copy an earlier pull made before orders carried their schedule columns: pulled again, it gains them. */
function pulledBefore(existing: AppUiRecordDoc, pulled: PulledOrder): boolean {
  const state = existing.state as PulledOrder['snapshot'] | undefined;
  return (
    !!existing.fromServer &&
    !state?.schedule?.columns &&
    !!pulled.snapshot.schedule?.columns
  );
}

function orderIdOf(doc: AppUiRecordDoc): string {
  return idOf((doc.state as { orderId?: unknown } | null)?.orderId);
}
